import { VaccineCampaignStoryblok } from "@/types/component-types"

/**
 * Whether a vaccine's campaign is running right now.
 *
 * One rule, shared by the price card and the vaccine detail table: those two render the same prices
 * from the same story, so a campaign that counts in one place and not the other is a bug the reader
 * sees as two different prices for one vaccine.
 *
 * A description and an end date are always required: the description is what the page tells the
 * reader the campaign is, and a campaign with no end never ends, which is not something an editor
 * should be able to leave behind by forgetting a field. What decides between them is either the
 * start date, when one is set, or `campaign_enabled` when it is not.
 */
export type CampaignFields = Pick<
  VaccineCampaignStoryblok,
  "campaign_enabled" | "campaign_start_date" | "campaign_end_date" | "campaign_description" | "campaign_price"
>

/** The clinics are Swedish and so are the campaign dates an editor types. */
const CLINIC_TIME_ZONE = "Europe/Stockholm"

/**
 * Today as "YYYY-MM-DD" in Swedish time, whatever the runtime's own zone is.
 *
 * Dates are compared as strings rather than as `Date` objects on purpose. `new Date("2026-10-15")`
 * with no offset is parsed in the engine's local zone — UTC in the container, Europe/Stockholm in
 * the reader's browser — so the same campaign could be active on the server and finished in the
 * browser for an hour or two around midnight. `VaccinationPriceCard` is a client component rendered
 * first on the server and then hydrated, so that difference would surface as a hydration mismatch,
 * and the price card and the detail table could disagree about one vaccine.
 *
 * `sv-SE` formats as "YYYY-MM-DD", which compares correctly as a plain string.
 */
const todayInClinicTime = () => new Intl.DateTimeFormat("sv-SE", { timeZone: CLINIC_TIME_ZONE }).format(new Date())

/** Storyblok writes a datetime as "YYYY-MM-DD HH:mm"; only the date half is meaningful here. */
const campaignDay = (value: string) => {
  const day = value.split(" ")[0]
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

export const isCampaignActive = (vaccine: CampaignFields | undefined) => {
  if (!vaccine?.campaign_description?.length) return false
  if (!vaccine.campaign_end_date) return false

  const today = todayInClinicTime()

  // Exclusive: an end date of the 15th means the campaign ended at 00:00 on the 15th, Swedish time.
  // Same rule as dse-web-svc's expire-campaigns.
  const end = campaignDay(vaccine.campaign_end_date)
  if (end === null || end <= today) return false

  // A start date takes over from the switch: an editor who scheduled a campaign has said when it
  // runs, and leaving `campaign_enabled` as the gate would mean someone still has to remember to
  // flip it on that morning. Without a start date the switch is all there is.
  if (vaccine.campaign_start_date) {
    const start = campaignDay(vaccine.campaign_start_date)
    return start !== null && start <= today
  }

  return vaccine.campaign_enabled === true
}

/** The discount to subtract, or nothing when no campaign is running or no amount was filled in. */
export const campaignDiscount = (vaccine: CampaignFields | undefined) =>
  isCampaignActive(vaccine) && vaccine?.campaign_price ? vaccine.campaign_price : undefined
